import { z } from 'zod';

/**
 * Zod schemas for external public auth pages (login, forgot, reset, accept-invite).
 *
 * These are intentionally LOOSER than the backend Zod schemas — they exist for UX
 * immediacy. The server (Phase 14) is the source of truth for password length /
 * email format / token validity / anti-enumeration.
 */

export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Please enter a valid email address.')
    .email('Please enter a valid email address.'),
  password: z.string().min(1, 'Password is required.').max(1024),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'Please enter a valid email address.')
    .email('Please enter a valid email address.'),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

// Reset-password and accept-invite share the same shape on the client.
// The client maps `newPassword` -> `password` at the API call boundary for
// accept-invite (server takes `password` for invite, `newPassword` for reset).
export const passwordSetSchema = z
  .object({
    newPassword: z.string().min(8, 'Password must be at least 8 characters.').max(1024),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'],
  });
export type PasswordSetInput = z.infer<typeof passwordSetSchema>;
